// src/pages/Campaigns/SurveyGenie/SurveySmsWizard.js
import React, { useState, useEffect } from 'react';
import {
  Box, Stepper, Step, StepLabel, Button, Typography, Paper,
  TextField, Alert, CircularProgress, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Checkbox,
  TablePagination, Chip, IconButton, Tabs, Tab
} from '@mui/material';
import { Sms, CheckCircle, ArrowBack } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../../utils/axiosInstance';

const steps = ['Select Customers', 'Connect SMS', 'Compose', 'Send & Review'];

const SurveySmsWizard = () => {
  const { chatbotId, surveyId } = useParams();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);

  // Data States
  const [contacts, setContacts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]); 
  const [smsConfigured, setSmsConfigured] = useState(false);
  const [checkingConfig, setCheckingConfig] = useState(false);
  const [messageBody, setMessageBody] = useState('We value your feedback! Please take our survey: {{survey_link}}');
  
  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [sourceTab, setSourceTab] = useState(0); // 0 = Consented, 1 = CRM
  const [crmContacts, setCrmContacts] = useState([]);
  const [selectedCrmIds, setSelectedCrmIds] = useState([]);

  useEffect(() => {
    if (activeStep === 0) {
        if (sourceTab === 0) loadContacts(); 
        if (sourceTab === 1) loadCrmContacts(); 
    }
    if (activeStep === 1) checkSmsStatus();
  }, [activeStep, sourceTab]);

  const loadContacts = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get('/api/user/subscriptions');
      const validContacts = (res.data.data || []).filter(c => c.phone && c.is_active);
      setContacts(validContacts);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // Load CRM Data
  const loadCrmContacts = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get('/api/crm/contacts', { params: { chatbot_id: chatbotId, per_page: 1000 } });
      // Filter for valid phones
      const valid = (res.data.data || []).filter(c => c.phone || c.phone_mobile);
      setCrmContacts(valid);
    } catch (e) { console.error(e); } 
    finally { setLoading(false); }
  };

  const checkSmsStatus = async () => {
    setCheckingConfig(true);
    try {
      const res = await axiosInstance.get('/sms/service/status');
      setSmsConfigured(res.data.data?.is_configured || false);
    } catch (e) {
      console.error("SMS Status Check Failed:", e);
      setSmsConfigured(false);
    } finally {
      setCheckingConfig(false);
    }
  };

  const handleSend = async () => {
    try {
      setLoading(true);
      await axiosInstance.post('/api/surveys/distribute/sms', {
        survey_id: surveyId,
        subscription_ids: sourceTab === 0 ? selectedIds : [], // Only send if source is consented
        crm_contact_ids: sourceTab === 1 ? selectedCrmIds : [], // Send CRM IDs
        message: messageBody
      }, { params: { chatbot_id: chatbotId } });
      
      alert('SMS Campaign Sent Successfully!');
      navigate(`/chatbot/${chatbotId}/surveys`);
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to send SMS');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
        if (sourceTab === 0 && selectedIds.length === 0) return alert('Select at least one contact');
        if (sourceTab === 1 && selectedCrmIds.length === 0) return alert('Select at least one CRM contact');
    }
    if (activeStep === 1 && !smsConfigured) return alert('Please connect SMS first');
    if (activeStep === 2 && !messageBody.includes('{{survey_link}}')) return alert('Message must contain {{survey_link}}');
    
    setActiveStep((prev) => prev + 1);
  };

  // --- Render Steps ---

  const renderSelectStep = () => (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={sourceTab} onChange={(e, v) => { setSourceTab(v); setPage(0); }} variant="scrollable" scrollButtons="auto">
              <Tab label="Consented Contacts" />
              <Tab label="CRM Contacts" />
          </Tabs>
      </Box>

      {loading ? <CircularProgress /> : (
        <Paper variant="outlined">
          <TableContainer sx={{ maxHeight: 400, overflowX: 'auto' }}>
            <Table stickyHeader size="small" sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox 
                      checked={
                        sourceTab === 0 
                        ? (contacts.length > 0 && selectedIds.length === contacts.length)
                        : (crmContacts.length > 0 && selectedCrmIds.length === crmContacts.length)
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                            if(sourceTab === 0) setSelectedIds(contacts.map(c => c.id));
                            else setSelectedCrmIds(crmContacts.map(c => c.id));
                        } else {
                            if(sourceTab === 0) setSelectedIds([]);
                            else setSelectedCrmIds([]);
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>{sourceTab === 0 ? 'Phone' : 'Name'}</TableCell>
                  <TableCell>{sourceTab === 0 ? 'Source' : 'Phone'}</TableCell>
                  <TableCell>{sourceTab === 0 ? 'Date' : 'Company'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sourceTab === 0 
                 ? /* Logic for Consented Contacts */
                   contacts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                  <TableRow key={row.id} hover onClick={() => {
                      const selectedIndex = selectedIds.indexOf(row.id);
                      let newSelected = [];
                      if (selectedIndex === -1) newSelected = newSelected.concat(selectedIds, row.id);
                      else if (selectedIndex === 0) newSelected = newSelected.concat(selectedIds.slice(1));
                      else if (selectedIndex === selectedIds.length - 1) newSelected = newSelected.concat(selectedIds.slice(0, -1));
                      else newSelected = newSelected.concat(selectedIds.slice(0, selectedIndex), selectedIds.slice(selectedIndex + 1));
                      setSelectedIds(newSelected);
                  }} role="checkbox" aria-checked={selectedIds.indexOf(row.id) !== -1} selected={selectedIds.indexOf(row.id) !== -1} sx={{ cursor: 'pointer' }}>
                    <TableCell padding="checkbox">
                      <Checkbox checked={selectedIds.indexOf(row.id) !== -1} />
                    </TableCell>
                    <TableCell>{row.phone}</TableCell>
                    <TableCell><Chip label={row.source} size="small" /></TableCell>
                    <TableCell>{new Date(row.created_at).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))
                 : /* Logic for CRM Contacts */
                   crmContacts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                      <TableRow key={row.id} hover role="checkbox" onClick={() => {
                          const selectedIndex = selectedCrmIds.indexOf(row.id);
                          let newSelected = [];
                          if (selectedIndex === -1) newSelected = newSelected.concat(selectedCrmIds, row.id);
                          else newSelected = selectedCrmIds.filter(id => id !== row.id);
                          setSelectedCrmIds(newSelected);
                      }}>
                          <TableCell padding="checkbox"><Checkbox checked={selectedCrmIds.includes(row.id)} /></TableCell>
                          <TableCell>{row.first_name} {row.last_name}</TableCell>
                          <TableCell>{row.phone_mobile || row.phone}</TableCell>
                          <TableCell>{row.account?.name || '-'}</TableCell>
                      </TableRow>
                   ))
                }
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={sourceTab === 0 ? contacts.length : crmContacts.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          />
        </Paper>
      )}
      <Typography variant="subtitle2" sx={{ mt: 2 }}>
        Selected: {sourceTab === 0 ? selectedIds.length : selectedCrmIds.length}
      </Typography>
    </Box>
  );

  const renderConnectStep = () => (
    <Box textAlign="center" py={5}>
      {checkingConfig ? <CircularProgress /> : (
        <>
          <Sms sx={{ fontSize: 60, color: smsConfigured ? 'success.main' : 'grey.400', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            {smsConfigured ? 'SMS Service Connected' : 'Connect SMS Service'}
          </Typography>
          
          {smsConfigured ? (
             <Alert severity="success" sx={{ display: 'inline-flex', mt: 2 }} icon={<CheckCircle />}>
                Ready to send messages
             </Alert>
          ) : (
            <Box mt={2}>
                <Alert severity="warning" sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}>
                    You have not configured an SMS provider (Twilio, AWS, etc.) yet.
                </Alert>
                <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={() => {
                        navigate(`/chatbot/${chatbotId}/campaigns/Sms`); 
                    }}
                >
                    Go to SMS Configuration
                </Button>
                <Button onClick={checkSmsStatus} sx={{ ml: 2 }}>
                    Check Again
                </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );

  const renderComposeStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Compose Message</Typography>
      <TextField
        fullWidth
        multiline
        rows={4}
        label="SMS Content"
        value={messageBody}
        onChange={(e) => setMessageBody(e.target.value)}
        helperText={`${messageBody.length} characters. Ensure {{survey_link}} is included.`}
        error={!messageBody.includes('{{survey_link}}')}
      />
      <Alert severity="info" sx={{ mt: 2 }}>
        Variable <strong>{`{{survey_link}}`}</strong> will be replaced with the unique survey URL for each recipient.
      </Alert>
    </Box>
  );

  const renderReviewStep = () => (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        You are about to send SMS to <strong>{sourceTab === 0 ? selectedIds.length : selectedCrmIds.length}</strong> contacts.
      </Alert>
      <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5' }}>
        <Typography variant="caption" color="text.secondary">Preview:</Typography>
        <Typography variant="body1" sx={{ mt: 1 }}>{messageBody}</Typography>
      </Paper>
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
      {/* HEADER WITH BACK BUTTON */}
      <Box display="flex" alignItems="flex-start" mb={4} flexWrap="wrap" gap={2}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
            <ArrowBack />
        </IconButton>
        <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            Send Survey SMS
        </Typography>
      </Box>

      <Stepper activeStep={activeStep} sx={{ mb: 5 }}>
        {steps.map((label) => (<Step key={label}><StepLabel>{label}</StepLabel></Step>))}
      </Stepper>

      <Paper sx={{ p: 3, mb: 3, minHeight: 300 }}>
        {activeStep === 0 && renderSelectStep()}
        {activeStep === 1 && renderConnectStep()}
        {activeStep === 2 && renderComposeStep()}
        {activeStep === 3 && renderReviewStep()}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button onClick={() => activeStep === 0 ? navigate(-1) : setActiveStep(prev => prev - 1)}>
          Back
        </Button>
        {activeStep === steps.length - 1 ? (
          <Button variant="contained" onClick={handleSend} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Send SMS'}
          </Button>
        ) : (
          <Button variant="contained" onClick={handleNext} disabled={activeStep === 1 && !smsConfigured}>
            Next
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default SurveySmsWizard;